import { CanvasImageValue } from '@seaters/constants';
import React, { type JSX } from 'react';
import { DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import {
  Image,
  Spin,
  Upload,
  theme,
  UploadProps,
  Space,
  FormItemProps,
  Row,
  Form,
  Col,
  Typography,
} from 'antd';
import {
  Button,
  baseColor400,
  colorFillQuaternary,
  secondaryColor,
} from '@seaters/ui';
import styles from './styles.module.css';
import { useTranslation } from 'react-i18next';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';

const { Dragger } = Upload;
const { Text } = Typography;

interface ImageUploaderProps {
  color?: string;
  width: number;
  height: number;
  remove: () => void;
  className: string;
  isLoading?: boolean;
  handleChange: (info: UploadChangeParam<UploadFile>) => void;
  beforeUpload?: UploadProps['beforeUpload'];
  uploadText?: string | JSX.Element;
  accept?: UploadProps['accept'];
  formItemProps?: FormItemProps;
  imageOrCanvas: CanvasImageValue | string | null | undefined;
  customRequest: UploadProps['customRequest'];
  listType?: UploadProps['listType'];
  sideContent?: React.ReactNode;
  size: string;
}

export const ImageUploader = ({
  color,
  handleChange,
  remove,
  width,
  height,
  isLoading = false,
  beforeUpload,
  uploadText,
  accept,
  formItemProps,
  customRequest,
  imageOrCanvas,
  sideContent,
  listType,
  size,
}: ImageUploaderProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const isSmallDragAndDropArea = width < 150 || height < 150;
  const isPictureCircle = listType === 'picture-circle';

  const renderUploadContent = () => {
    if (isSmallDragAndDropArea || isPictureCircle) {
      return (
        <div>
          <PictureOutlined
            className="text-[28px]"
            style={{ color: color || secondaryColor }}
          />
          <Text className="text-[10px] block">{size}</Text>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-4">
        <div className="size-12 flex items-center justify-center">
          <PictureOutlined
            className="text-[42px]"
            style={{ color: color || secondaryColor }}
          />
        </div>

        <div className="flex flex-col items-center gap-1">
          <Text className="text-sm text-center leading-[22px]">
            {uploadText || t('drop_file_info_message')}
          </Text>
        </div>
      </div>
    );
  };

  return (
    <Row gutter={24} style={{ maxWidth: '100%' }} align="middle">
      <Col>
        <Spin wrapperClassName={styles.spin} spinning={isLoading}>
          <Space direction="vertical" size="small" style={{ maxWidth: '100%' }}>
            <div
              style={{
                width: isPictureCircle ? 102 : width,
                height: isPictureCircle ? 102 : 'unset',
                maxWidth: '100%',
                aspectRatio: width / height,
              }}
            >
              {imageOrCanvas ? (
                <Image
                  width={'100%'}
                  src={
                    imageOrCanvas && typeof imageOrCanvas !== 'string'
                      ? imageOrCanvas?.toDataURL()
                      : imageOrCanvas
                  }
                  style={{
                    borderRadius: listType === 'picture-circle' ? '50%' : 12,
                    maxWidth: '100%',
                    border: `1px solid ${baseColor400}`,
                  }}
                />
              ) : isPictureCircle ? (
                <Upload
                  multiple={false}
                  customRequest={customRequest}
                  listType="picture-circle"
                  onChange={handleChange}
                  beforeUpload={beforeUpload}
                  accept={accept}
                  showUploadList={false}
                  name={formItemProps?.name}
                  id={formItemProps?.id}
                  style={{
                    ...(formItemProps?.validateStatus === 'error'
                      ? { borderColor: token.colorError }
                      : {}),
                  }}
                >
                  {renderUploadContent()}
                </Upload>
              ) : (
                <Dragger
                  multiple={false}
                  customRequest={customRequest}
                  onChange={handleChange}
                  beforeUpload={beforeUpload}
                  accept={accept}
                  showUploadList={false}
                  name={formItemProps?.name}
                  id={formItemProps?.id}
                  style={{
                    backgroundColor: colorFillQuaternary,
                    ...(formItemProps?.validateStatus === 'error'
                      ? { borderColor: token.colorError }
                      : {}),
                  }}
                >
                  {renderUploadContent()}
                </Dragger>
              )}
            </div>
            {imageOrCanvas ? (
              <Button
                danger
                type="text"
                onClick={remove}
                icon={<DeleteOutlined rev={undefined} />}
              >
                {t('remove_image_button_text')}
              </Button>
            ) : null}
          </Space>
        </Spin>
      </Col>
      {sideContent && (
        <Col xs={16} md={16} lg={12} className={styles.centeredCol}>
          <Form.Item>
            <Text type="secondary" className="text-xs w-full">
              {sideContent}
            </Text>
          </Form.Item>
        </Col>
      )}
      {formItemProps?.help && (
        <span style={{ color: token.colorError }}> {formItemProps?.help}</span>
      )}
    </Row>
  );
};
